
import {Prop, Ref, Vue} from 'vue-property-decorator';
import Component from 'vue-class-component';
import {QChip} from 'quasar';

  type Circle = {
    x: number;
    y: number;
    delay: number;
    color: string;
    radius: number;
  };

@Component
export default class ScoreAnimation extends Vue {
  /* Canvas Handling */
  @Ref() container: HTMLDivElement;
  @Ref() canvas: HTMLCanvasElement;
  canvasSize: {width: number; height: number} = {width: 0, height: 0};
  context: CanvasRenderingContext2D;

  /* Score Handling */
  @Prop() scoreSpan: HTMLSpanElement;
  @Prop() scoreChip: QChip;
  @Prop() score: number;
  @Prop() scoreDiff: number;
  displayedScoreDiff: {value: number} = {value: 0};
  displayedScore: {value: number} = {value: 0};

  /* Canvas Animations */
  @Ref() scoreDiffElement: HTMLDivElement;
  @Prop() scorePosition: {top: number; left: number};
  scoreDiffVibratingArray: Circle[] = [];
  scoreParticleArray: Circle[] = [];

  /* Main Timeline Animation */
  animationTimeline: any;
  animationFrame: any;

  mounted() {
    this.initCanvas();
    this.initAnimationTimeline();
    this.launchAnimationTimeline();
  }

  initAnimationTimeline() {
    this.animationTimeline = this.$anime.timeline({autoplay: false});

    // Score Diff
    this.animationTimeline.add({
      targets: this.scoreDiffElement,
      top: ['50%', '50%'],
      left: ['50%', '50%'],
      height: [0, 50],
      width: [0, 50],
      fontSize: [0, '1.3em'],
      duration: 750,
      easing: 'easeInElastic(1, .6)',
    });
    this.animationTimeline.add({
      targets: this.displayedScoreDiff,
      duration: 1500,
      round: 1,
      easing: 'easeInCubic',
      value: this.scoreDiff,
    });

    // Canvas Animation
    this.animationTimeline.add(this.getScoreDiffRoundAnimation(), '-=1500');
    this.animationTimeline.add(this.getScoreParticlesAnimation(), '-=1500');

    // Score Diff Close
    this.animationTimeline.add(
      {
        targets: this.scoreDiffElement,
        top: [this.canvas.height / 2 + 'px', this.scorePosition.top - 12 + 'px'],
        left: [this.canvas.width / 2 + 'px', this.scorePosition.left + 14 + 'px'],
        scale: [1, 0.75],
        translateX: ['-50%', '-50%'],
        translateY: ['-50%', '-50%'],
        height: [50, 30],
        width: [50, 30],
        fontSize: ['1.3em', '1em'],
        duration: 500,
        easing: 'easeInElastic(1, .6)',
      },
      '-=750'
    );
    this.animationTimeline.add(
      {
        targets: this.scoreChip.$el,
        keyframes: [
          {scale: 1.2},
          {rotate: 10},
          {rotate: 0},
          {rotate: -10},
          {rotate: 0},
          {rotate: 10},
          {rotate: 0},
          {rotate: -10},
          {rotate: 0},
          {scale: 1},
        ],
        duration: 500,
        round: 1,
        loop: 2,
        direction: 'alternate',
        easing: 'linear',
      },
      '-=150'
    );

    // Score Increase
    this.animationTimeline.add(
      {
        targets: this.displayedScore,
        duration: 2000,
        round: 1,
        easing: 'easeInCubic',
        value: this.score,
        update: () => {
          this.scoreSpan.innerHTML = this.displayedScore.value.toString();
          this.$emit('scoreChange', this.displayedScore.value);
        },
      },
      500
    );
    this.animationTimeline.finished.then(() => cancelAnimationFrame(this.animationFrame));
  }
  launchAnimationTimeline() {
    this.animationTimeline.restart();
    const loop = (t) => {
      this.clearCanvas();
      this.animationTimeline.tick(t);
      this.animationFrame = requestAnimationFrame(loop);
    };
    this.animationFrame = requestAnimationFrame(loop);
  }

  /* Canvas Animations */
  getScoreDiffRoundAnimation() {
    /* All Animation Parameters */
    const xyDelta = 5;
    const circleRadius = 17;
    const circleQuantity = 15;
    const eachAnimationDuration = 800;
    const staggerGap = 100;

    /* Init Animation Data */
    const getControlledRandomXY = (counter: number) => {
      switch (counter % 4) {
        case 0:
          return {
            x: this.$anime.random(this.centerX, this.centerX - xyDelta),
            y: this.$anime.random(this.centerY, this.centerY + xyDelta),
          };
        case 1:
          return {
            x: this.$anime.random(this.centerX, this.centerX + xyDelta),
            y: this.$anime.random(this.centerY, this.centerY + xyDelta),
          };
        case 2:
          return {
            x: this.$anime.random(this.centerX, this.centerX + xyDelta),
            y: this.$anime.random(this.centerY, this.centerY - xyDelta),
          };
        case 3:
          return {
            x: this.$anime.random(this.centerX, this.centerX - xyDelta),
            y: this.$anime.random(this.centerY, this.centerY - xyDelta),
          };
      }
    };
    this.scoreDiffVibratingArray = [];
    for (let i = 0; i < circleQuantity; i++) {
      const pos = getControlledRandomXY(i);
      this.scoreDiffVibratingArray.push({
        x: pos.x,
        y: pos.y,
        delay: 0, // Should it be removed ? // no will be overrided by stagger
        color: this.getRandomColor(),
        radius: circleRadius,
      } as Circle);
    }

    /* Return AnimeJS Object */
    return {
      targets: this.scoreDiffVibratingArray,
      easing: 'easeInOutSine',
      keyframes: [{radius: 2 * circleRadius}, {radius: circleRadius}],
      delay: this.$anime.stagger(staggerGap), // Adjust Duration, delay & quantity to match what we want in term of timing
      duration: eachAnimationDuration,
      update: (anim) => {
        if (anim.completed) {
          // Strangly need to force this cause update still going on
          return;
        }
        for (const animElement of this.scoreDiffVibratingArray) {
          if (animElement.delay < anim.currentTime) {
            this.drawCircle(animElement.x, animElement.y, animElement.radius, animElement.color);
          }
        }
      },
    };
  }
  getScoreParticlesAnimation() {
    /* All Animation Parameters */
    const particleRadius = 5;
    const particleQty = 50;
    const animationDuration = 2000;
    const xRange = [particleRadius, this.canvasSize.width - particleRadius];
    const yRange = [particleRadius, this.canvasSize.height - particleRadius];

    /* Init Particles */
    this.scoreParticleArray = [];
    for (let i = 0; i < particleQty; i++) {
      this.scoreParticleArray.push({
        x: this.$anime.random(xRange[0], xRange[1]),
        y: this.$anime.random(yRange[0], yRange[1]),
        delay: this.$anime.random(0, animationDuration - 500),
        color: this.getRandomColor(),
        radius: particleRadius,
      } as Circle);
    }

    /* Return AnimeJS Object */
    return {
      targets: this.scoreParticleArray,
      x: this.scorePosition.left + 20,
      y: this.scorePosition.top + 20,
      round: 1,
      easing: 'easeInOutExpo',
      delay: (el) => el.delay,
      duration: (el) => animationDuration - el.delay,
      update: (anim) => {
        if (anim.completed) {
          // Strangly need to force this cause update still going on
          return;
        }
        for (const animElement of this.scoreParticleArray) {
          if (animElement.delay + 20 < anim.currentTime) {
            this.drawCircle(animElement.x, animElement.y, particleRadius, animElement.color);
          }
        }
      },
    };
  }

  /* Canvas Utils */
  initCanvas() {
    this.canvasSize.width = this.container.clientWidth;
    this.canvasSize.height = this.container.clientHeight;
    this.canvas.width = this.canvasSize.width;
    this.canvas.height = this.canvasSize.height;
    this.context = this.canvas.getContext('2d');
  }

  get centerX() {
    return this.canvasSize.width / 2;
  }
  get centerY() {
    return this.canvasSize.height / 2;
  }

  private clearCanvas() {
    this.context.clearRect(0, 0, this.canvasSize.width, this.canvasSize.height);
  }
  private drawCircle(x: number, y: number, radius: number, color: string) {
    this.context.fillStyle = color;
    this.context.beginPath();
    this.context.arc(x, y, radius, 0, 2 * Math.PI, false);
    this.context.fill();
  }

  /* Anim Utils */

  getRandomColor() {
    const possibleColors = ['#F9DC5C', '#00BBF9', '#DF2935', '#17B890', '#EC058E'];
    return possibleColors[this.$anime.random(0, possibleColors.length)];
  }
}
